import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  CheckCircle,
  XCircle,
  Clock,
  ArrowUpRight,
  Eye,
  Filter,
  Briefcase,
  Building2,
  AlertTriangle,
  FileText,
  User,
  Users,
  History,
  Send,
  RotateCcw,
  ChevronRight
} from 'lucide-react';

const ESTADOS = [
  { value: 'PENDIENTE', label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  { value: 'APROBADA', label: 'Aprobada', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  { value: 'RECHAZADA', label: 'Rechazada', color: 'bg-red-100 text-red-800', icon: XCircle },
  { value: 'DEVUELTA', label: 'Devuelta', color: 'bg-orange-100 text-orange-800', icon: RotateCcw },
];

const TIPOS = [
  { value: 'CASO', label: 'Caso', icon: Briefcase },
  { value: 'EMPRESA', label: 'Empresa', icon: Building2 },
  { value: 'ALERTA', label: 'Alerta', icon: AlertTriangle },
  { value: 'DOCUMENTO', label: 'Documento', icon: FileText },
];

const PRIORIDADES = [
  { value: 'CRITICA', label: 'Crítica', color: 'bg-red-100 text-red-800' },
  { value: 'ALTA', label: 'Alta', color: 'bg-orange-100 text-orange-800' },
  { value: 'MEDIA', label: 'Media', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'BAJA', label: 'Baja', color: 'bg-green-100 text-green-800' },
];

const AprobacionesPage = () => {
  const navigate = useNavigate();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [filters, setFilters] = useState({ estado: '', tipo: '', mis_pendientes: false });
  
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  const [decisionType, setDecisionType] = useState('');
  const [decisionComment, setDecisionComment] = useState('');
  
  const [showEscalateDialog, setShowEscalateDialog] = useState(false);
  const [escalateData, setEscalateData] = useState({ grupo_destino_id: '', motivo: '', prioridad: '' });
  const [groups, setGroups] = useState([]);

  const loadApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.estado) params.estado = filters.estado;
      if (filters.tipo) params.tipo = filters.tipo;
      if (filters.mis_pendientes) params.mis_pendientes = true;
      
      const [approvalsRes, countRes] = await Promise.all([
        api.get('/approvals', { params }),
        api.get('/approvals/pending-count')
      ]);
      
      setApprovals(approvalsRes.data);
      setPendingCount(countRes.data.count);
    } catch (error) {
      toast.error('Error cargando aprobaciones');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadGroups = async () => {
    try {
      const res = await api.get('/groups');
      setGroups(res.data);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  useEffect(() => {
    loadApprovals();
    loadGroups();
  }, [loadApprovals]);

  const handleDecision = async () => {
    if (!selectedApproval || !decisionType) return;
    
    try {
      await api.post(`/approvals/${selectedApproval.id}/decide`, {
        decision: decisionType,
        comentario: decisionComment
      });
      
      toast.success(`Solicitud ${decisionType.toLowerCase()}`);
      setShowDecisionDialog(false);
      setShowDetailDialog(false);
      setDecisionComment('');
      loadApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar decisión');
    }
  };

  const handleEscalate = async () => {
    if (!selectedApproval || !escalateData.grupo_destino_id || !escalateData.motivo) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    
    try {
      await api.post(`/approvals/${selectedApproval.id}/escalate`, escalateData);
      toast.success('Solicitud escalada correctamente');
      setShowEscalateDialog(false);
      setShowDetailDialog(false);
      setEscalateData({ grupo_destino_id: '', motivo: '', prioridad: '' });
      loadApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al escalar');
    }
  };

  const openDetail = (approval) => {
    setSelectedApproval(approval);
    setShowDetailDialog(true);
  };

  const openDecisionDialog = (type) => {
    setDecisionType(type);
    setShowDecisionDialog(true);
  };

  const getEstadoStyle = (estado) => {
    return ESTADOS.find(e => e.value === estado) || ESTADOS[0];
  };

  const getTipoStyle = (tipo) => {
    return TIPOS.find(t => t.value === tipo) || TIPOS[0];
  };

  const getPrioridadStyle = (prioridad) => {
    return PRIORIDADES.find(p => p.value === prioridad) || PRIORIDADES[2];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const navigateToEntity = (approval) => {
    if (approval.tipo === 'CASO') {
      navigate(`/m/gestion-de-casos/caso/${approval.entidad_id}`);
    } else if (approval.tipo === 'EMPRESA') {
      navigate(`/m/empresas/empresa/${approval.entidad_id}`);
    }
    setShowDetailDialog(false);
  };

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen" data-testid="approvals-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CheckCircle className="text-green-600" />
            Workflow de Aprobaciones
          </h1>
          <p className="text-slate-600 mt-1">
            Gestiona solicitudes de aprobación y derivaciones
          </p>
        </div>
        {pendingCount > 0 && (
          <div className="mt-4 lg:mt-0 px-4 py-2 bg-yellow-100 border border-yellow-300 rounded-lg">
            <span className="text-yellow-800 font-medium">
              {pendingCount} solicitud{pendingCount !== 1 ? 'es' : ''} pendiente{pendingCount !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card 
          className={`p-4 cursor-pointer transition-all ${filters.mis_pendientes ? 'ring-2 ring-blue-500 bg-blue-50' : 'bg-white hover:bg-slate-50'}`}
          onClick={() => setFilters(f => ({ ...f, mis_pendientes: !f.mis_pendientes, estado: '' }))}
          data-testid="filter-my-pending"
        >
          <div className="text-2xl font-bold text-blue-700">{pendingCount}</div>
          <div className="text-xs text-blue-600">Mis Pendientes</div>
        </Card>
        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="text-2xl font-bold text-yellow-700">
            {approvals.filter(a => a.estado === 'PENDIENTE').length}
          </div>
          <div className="text-xs text-yellow-600">Pendientes Total</div>
        </Card>
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="text-2xl font-bold text-green-700">
            {approvals.filter(a => a.estado === 'APROBADA').length}
          </div>
          <div className="text-xs text-green-600">Aprobadas</div>
        </Card>
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="text-2xl font-bold text-red-700">
            {approvals.filter(a => a.estado === 'RECHAZADA').length}
          </div>
          <div className="text-xs text-red-600">Rechazadas</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6 bg-white">
        <div className="flex flex-wrap gap-4 items-center">
          <Filter size={18} className="text-slate-400" />
          <Select 
            value={filters.estado || "all"} 
            onValueChange={(v) => setFilters(f => ({ ...f, estado: v === "all" ? "" : v }))}
          >
            <SelectTrigger className="w-48" style={{ backgroundColor: '#fff' }}>
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {ESTADOS.map(e => (
                <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select 
            value={filters.tipo || "all"} 
            onValueChange={(v) => setFilters(f => ({ ...f, tipo: v === "all" ? "" : v }))}
          >
            <SelectTrigger className="w-48" style={{ backgroundColor: '#fff' }}>
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {TIPOS.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={() => setFilters({ estado: '', tipo: '', mis_pendientes: false })}
          >
            Limpiar
          </Button>
        </div>
      </Card>

      {/* Approvals Table */}
      <Card className="bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Entidad</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Acción</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Prioridad</TableHead>
              <TableHead>Solicitante</TableHead>
              <TableHead>Grupo/Usuario</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead className="text-center">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : approvals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                  <CheckCircle size={40} className="mx-auto mb-2 text-slate-300" />
                  No hay solicitudes de aprobación
                </TableCell>
              </TableRow>
            ) : (
              approvals.map(approval => {
                const estadoStyle = getEstadoStyle(approval.estado);
                const tipoStyle = getTipoStyle(approval.tipo);
                const prioridadStyle = getPrioridadStyle(approval.prioridad);
                const TipoIcon = tipoStyle.icon;
                const EstadoIcon = estadoStyle.icon;
                
                return (
                  <TableRow key={approval.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="font-medium text-slate-900">{approval.entidad_nombre}</div>
                      <div className="text-xs text-slate-500 truncate max-w-[200px]">
                        {approval.descripcion}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 text-sm text-slate-700">
                        <TipoIcon size={14} />
                        {tipoStyle.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {approval.accion}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${estadoStyle.color}`}>
                        <EstadoIcon size={12} />
                        {estadoStyle.label}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${prioridadStyle.color}`}>
                        {prioridadStyle.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {approval.solicitante_email}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {approval.grupo_aprobador_id ? (
                        <span className="flex items-center gap-1">
                          <Users size={14} className="text-blue-500" />
                          {groups.find(g => g.id === approval.grupo_aprobador_id)?.nombre || 'Grupo'}
                        </span>
                      ) : approval.usuario_aprobador_id ? (
                        <span className="flex items-center gap-1">
                          <User size={14} className="text-green-500" />
                          Usuario
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatDate(approval.fecha_creacion)}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetail(approval)}
                          data-testid={`view-approval-${approval.id}`}
                        >
                          <Eye size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#ffffff' }}>
          {selectedApproval && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle className="text-green-600" />
                  Detalle de Solicitud
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Estado y Prioridad */}
                <div className="flex flex-wrap gap-4 items-center">
                  <div>
                    <Label className="text-xs text-slate-500">Estado</Label>
                    <div className={`px-3 py-2 rounded text-sm font-medium inline-flex items-center gap-1 ${getEstadoStyle(selectedApproval.estado).color}`}>
                      {React.createElement(getEstadoStyle(selectedApproval.estado).icon, { size: 14 })}
                      {getEstadoStyle(selectedApproval.estado).label}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Prioridad</Label>
                    <div className={`px-3 py-2 rounded text-sm font-medium ${getPrioridadStyle(selectedApproval.prioridad).color}`}>
                      {getPrioridadStyle(selectedApproval.prioridad).label}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Tipo</Label>
                    <div className="px-3 py-2 rounded text-sm font-medium bg-slate-100 text-slate-700 inline-flex items-center gap-1">
                      {React.createElement(getTipoStyle(selectedApproval.tipo).icon, { size: 14 })}
                      {getTipoStyle(selectedApproval.tipo).label}
                    </div>
                  </div>
                </div>

                {/* Entidad */}
                <Card className="p-4 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs text-slate-500">Entidad</Label>
                      <p className="font-medium text-slate-900">{selectedApproval.entidad_nombre}</p>
                      <p className="text-sm text-slate-600 mt-1">{selectedApproval.descripcion}</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigateToEntity(selectedApproval)}
                      data-testid="goto-entity-btn"
                    >
                      Ver Entidad
                      <ChevronRight size={16} className="ml-1" />
                    </Button>
                  </div>
                </Card>

                {/* Información */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Solicitante</Label>
                    <p className="text-sm text-slate-700">{selectedApproval.solicitante_email}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Acción Solicitada</Label>
                    <p className="text-sm text-slate-700">{selectedApproval.accion}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Fecha Creación</Label>
                    <p className="text-sm text-slate-700">{formatDate(selectedApproval.fecha_creacion)}</p>
                  </div>
                  {selectedApproval.fecha_decision && (
                    <div>
                      <Label className="text-xs text-slate-500">Fecha Decisión</Label>
                      <p className="text-sm text-slate-700">{formatDate(selectedApproval.fecha_decision)}</p>
                    </div>
                  )}
                  {selectedApproval.decidido_por && (
                    <div>
                      <Label className="text-xs text-slate-500">Decidido Por</Label>
                      <p className="text-sm text-slate-700">{selectedApproval.decidido_por}</p>
                    </div>
                  )}
                  {selectedApproval.escalaciones > 0 && (
                    <div>
                      <Label className="text-xs text-slate-500">Escalaciones</Label>
                      <p className="text-sm text-slate-700">{selectedApproval.escalaciones}</p>
                    </div>
                  )}
                </div>

                {/* Historial */}
                {selectedApproval.historial && selectedApproval.historial.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-1 mb-3">
                      <History size={14} />
                      Historial de Aprobación
                    </Label>
                    <div className="space-y-3">
                      {selectedApproval.historial.map((event, idx) => (
                        <div key={idx} className="relative pl-6 pb-3 border-l-2 border-slate-200 last:border-l-0 last:pb-0">
                          {/* Dot indicator */}
                          <div className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full border-2 border-white ${
                            event.accion === 'APROBADO' ? 'bg-green-500' :
                            event.accion === 'RECHAZADO' ? 'bg-red-500' :
                            event.accion === 'DEVUELTO' ? 'bg-orange-500' :
                            event.accion === 'ESCALADO' ? 'bg-purple-500' :
                            'bg-blue-500'
                          }`} />
                          
                          <div className="bg-slate-50 rounded-lg p-3">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                event.accion === 'APROBADO' ? 'bg-green-100 text-green-700' :
                                event.accion === 'RECHAZADO' ? 'bg-red-100 text-red-700' :
                                event.accion === 'DEVUELTO' ? 'bg-orange-100 text-orange-700' :
                                event.accion === 'ESCALADO' ? 'bg-purple-100 text-purple-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {event.accion}
                              </span>
                              <span className="text-xs text-slate-400">
                                {formatDate(event.fecha)}
                              </span>
                            </div>
                            {event.comentario && (
                              <p className="text-sm text-slate-600 mb-1">{event.comentario}</p>
                            )}
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <User size={10} />
                              {event.usuario}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Acciones si está pendiente */}
                {selectedApproval.estado === 'PENDIENTE' && (
                  <div className="flex flex-wrap gap-2 pt-4 border-t">
                    <Button 
                      onClick={() => openDecisionDialog('APROBADO')}
                      style={{ backgroundColor: '#16a34a', color: '#ffffff' }}
                      className="text-white"
                      data-testid="approve-btn"
                    >
                      <CheckCircle size={16} className="mr-2" />
                      Aprobar
                    </Button>
                    <Button 
                      onClick={() => openDecisionDialog('RECHAZADO')}
                      style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
                      className="text-white"
                      data-testid="reject-btn"
                    >
                      <XCircle size={16} className="mr-2" />
                      Rechazar
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => openDecisionDialog('DEVUELTO')}
                      style={{ backgroundColor: '#ffffff', color: '#1e293b', borderColor: '#e2e8f0' }}
                      data-testid="return-btn"
                    >
                      <RotateCcw size={16} className="mr-2" />
                      Devolver
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setShowEscalateDialog(true)}
                      style={{ backgroundColor: '#ffffff', color: '#1e293b', borderColor: '#e2e8f0' }}
                      data-testid="escalate-btn"
                    >
                      <ArrowUpRight size={16} className="mr-2" />
                      Escalar
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Decision Dialog */}
      <Dialog open={showDecisionDialog} onOpenChange={setShowDecisionDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle>
              {decisionType === 'APROBADO' && 'Aprobar Solicitud'}
              {decisionType === 'RECHAZADO' && 'Rechazar Solicitud'}
              {decisionType === 'DEVUELTO' && 'Devolver Solicitud'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Comentario (opcional)</Label>
            <Textarea
              value={decisionComment}
              onChange={(e) => setDecisionComment(e.target.value)}
              placeholder="Agregue un comentario sobre su decisión..."
              rows={3}
              style={{ backgroundColor: '#fff' }}
            />
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDecisionDialog(false)}
              style={{ backgroundColor: '#ffffff', color: '#1e293b', borderColor: '#e2e8f0' }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDecision}
              style={{ 
                backgroundColor: decisionType === 'APROBADO' ? '#16a34a' : 
                                decisionType === 'RECHAZADO' ? '#dc2626' : '#2563eb',
                color: '#ffffff'
              }}
              className="text-white"
              data-testid="confirm-decision-btn"
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalate Dialog */}
      <Dialog open={showEscalateDialog} onOpenChange={setShowEscalateDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpRight className="text-orange-600" />
              Escalar Solicitud
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Grupo Destino *</Label>
              <Select 
                value={escalateData.grupo_destino_id} 
                onValueChange={(v) => setEscalateData(d => ({ ...d, grupo_destino_id: v }))}
              >
                <SelectTrigger style={{ backgroundColor: '#fff' }} data-testid="escalate-group-select">
                  <SelectValue placeholder="Seleccionar grupo" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Motivo de Escalamiento *</Label>
              <Textarea
                value={escalateData.motivo}
                onChange={(e) => setEscalateData(d => ({ ...d, motivo: e.target.value }))}
                placeholder="Explique por qué escala esta solicitud..."
                rows={3}
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            
            <div>
              <Label>Nueva Prioridad (opcional)</Label>
              <Select 
                value={escalateData.prioridad || "same"} 
                onValueChange={(v) => setEscalateData(d => ({ ...d, prioridad: v === "same" ? "" : v }))}
              >
                <SelectTrigger style={{ backgroundColor: '#fff' }}>
                  <SelectValue placeholder="Mantener actual" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="same">Mantener actual</SelectItem>
                  {PRIORIDADES.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEscalateDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleEscalate}
              style={{ backgroundColor: '#f97316' }}
              data-testid="confirm-escalate-btn"
            >
              <ArrowUpRight size={16} className="mr-2" />
              Escalar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AprobacionesPage;
